import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  ArrowUpRight,
  ChevronRight,
  Globe,
  Languages,
  Star,
  TrendingUp,
  CheckCircle2,
} from "lucide-react";
import Navbar from "../components/homepage/Navbar";
import Footer from "../components/homepage/Footer";
import CTABanner from "../components/homepage/CTABanner";
import { MARKETS_CONFIG, MARKET_CODES } from "../data/markets";

const GLOBAL_STATS = [
  { icon: Globe, value: "10", label: "Active country markets" },
  { icon: TrendingUp, value: "200+", label: "Clients grown globally" },
  { icon: Languages, value: "8+", label: "Languages served" },
  { icon: Star, value: "4.9/5", label: "Average client rating" },
];

export default function AllMarketsPage() {
  const markets = MARKET_CODES.map((c) => MARKETS_CONFIG[c]);

  return (
    <>
      <Helmet>
        <title>
          Digital Marketing Agency in 10 Countries — USA, UK, UAE, Australia & more | Lumotis
        </title>
        <meta
          name="description"
          content="Lumotis delivers full-stack digital marketing in 10 countries — USA, UK, Australia, UAE, Canada, Germany, Japan, Singapore, Brazil and India. Localized strategies, local insights, local results."
        />
        <link rel="canonical" href="https://lumotis.com/markets" />
      </Helmet>

      <Navbar />

      <main data-testid="all-markets-page">

        {/* ══════════════════════════════════════ HERO */}
        <section className="relative bg-white pt-28 md:pt-36 pb-20 md:pb-24 overflow-hidden border-b border-zinc-100">
          <div className="absolute inset-0 bg-grid-pattern opacity-60 pointer-events-none" />
          <div className="absolute right-0 top-0 w-2/3 h-2/3 bg-gradient-to-bl from-electric/8 via-transparent to-transparent pointer-events-none" />
          <div
            aria-hidden
            className="absolute -left-32 bottom-0 w-[480px] h-[480px] glow-orb bg-electric/10 pointer-events-none"
          />

          <div className="container relative">
            <nav
              aria-label="Breadcrumb"
              className="flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-400 mb-8"
            >
              <Link to="/" className="hover:text-electric transition-colors">Home</Link>
              <ChevronRight className="w-3 h-3" />
              <span className="text-charcoal">Global Markets</span>
            </nav>

            <p className="text-overline text-electric mb-4">Global Markets</p>
            <motion.h1
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="font-display text-4xl sm:text-5xl lg:text-[62px] font-extrabold leading-[1.05] tracking-tight text-charcoal text-balance max-w-4xl"
            >
              10 countries.{" "}
              <span className="text-gradient">One digital marketing agency.</span>
            </motion.h1>
            <p className="mt-6 text-lg text-zinc-500 leading-relaxed max-w-2xl">
              From California to Tokyo, London to Dubai — we run full-stack digital marketing
              campaigns with local channel knowledge, local market data and local case studies
              in every country we serve. Pick yours below.
            </p>

            {/* Stats */}
            <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl">
              {GLOBAL_STATS.map((s) => {
                const Icon = s.icon;
                return (
                  <div
                    key={s.label}
                    className="rounded-2xl border border-zinc-100 bg-white p-5 md:p-6 shadow-card"
                  >
                    <Icon className="w-5 h-5 text-electric mb-3" strokeWidth={2} />
                    <p className="font-display text-3xl font-extrabold text-charcoal leading-none">
                      {s.value}
                    </p>
                    <p className="mt-2 text-[11px] uppercase tracking-widest text-zinc-500 leading-snug">
                      {s.label}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ══════════════════════════════════════ MARKETS GRID */}
        <section className="py-20 md:py-28 bg-white">
          <div className="container">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
              <div>
                <p className="text-overline text-electric mb-3">Browse markets</p>
                <h2 className="font-display text-3xl md:text-5xl font-extrabold text-charcoal leading-[1.05]">
                  Choose your country.
                </h2>
              </div>
              <p className="text-zinc-500 leading-relaxed max-w-sm">
                Each market page has country-specific channel insights, pricing in local
                currency, and a local client testimonial.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {markets.map((m, idx) => (
                <motion.div
                  key={m.code}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.35, delay: idx * 0.04 }}
                >
                  <Link
                    to={`/${m.code}`}
                    className="group rounded-2xl border border-zinc-100 bg-white p-7 md:p-8 flex flex-col h-full shadow-card hover:shadow-card-hover hover:border-electric/30 hover:-translate-y-1 transition-all"
                    data-testid={`market-card-${m.code}`}
                  >
                    {/* Flag + name */}
                    <div className="flex items-start justify-between mb-6">
                      <div>
                        <span className="text-5xl block leading-none mb-4" aria-hidden>
                          {m.flag}
                        </span>
                        <h3 className="font-display text-2xl font-extrabold text-charcoal group-hover:text-electric leading-tight transition-colors">
                          {m.name}
                        </h3>
                        <p className="text-xs uppercase tracking-widest text-zinc-400 mt-1">
                          {m.currency.trim()} · {m.locale.replace("_", "-")}
                        </p>
                      </div>
                      <ArrowUpRight className="w-5 h-5 text-zinc-300 group-hover:text-electric transition-colors" />
                    </div>

                    {/* Local stat */}
                    <div className="border-l-2 border-electric pl-3 mb-5">
                      <p className="font-display text-2xl font-extrabold text-charcoal leading-none">
                        {m.localStat.value}
                      </p>
                      <p className="text-xs text-zinc-500 mt-1 uppercase tracking-widest">
                        {m.localStat.label}
                      </p>
                    </div>

                    {/* Channels */}
                    <div className="flex flex-wrap gap-1.5 mb-5">
                      {m.localChannels.slice(0, 3).map((ch) => (
                        <span
                          key={ch}
                          className="inline-flex items-center gap-1 border border-zinc-100 bg-zinc-50 group-hover:border-electric/20 group-hover:bg-electric/5 text-[11px] text-zinc-600 group-hover:text-electric px-2.5 py-1 rounded-full font-medium transition-colors"
                        >
                          {ch}
                        </span>
                      ))}
                      {m.localChannels.length > 3 && (
                        <span className="inline-flex items-center border border-zinc-100 bg-zinc-50 text-[11px] text-zinc-400 px-2.5 py-1 rounded-full font-medium">
                          +{m.localChannels.length - 3} more
                        </span>
                      )}
                    </div>

                    {/* Emphasis */}
                    <p className="text-sm text-zinc-500 leading-relaxed flex-1">
                      {m.marketEmphasis}
                    </p>

                    {/* CTA */}
                    <div className="mt-6 pt-5 border-t border-zinc-100 flex items-center justify-between">
                      <span className="inline-flex items-center gap-1.5 text-xs font-bold text-charcoal group-hover:text-electric uppercase tracking-widest transition-colors">
                        View {m.shortName} strategy
                      </span>
                      <ArrowRight className="w-4 h-4 text-zinc-300 group-hover:text-electric transition-colors" />
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ══════════════════════════════════════ WHY LOCAL MATTERS */}
        <section className="py-20 md:py-28 bg-zinc-50 border-y border-zinc-100">
          <div className="container">
            <div className="grid lg:grid-cols-12 gap-12 items-center">
              <div className="lg:col-span-6">
                <p className="text-overline text-electric mb-4">Why localisation matters</p>
                <h2 className="font-display text-3xl md:text-5xl font-extrabold text-charcoal leading-[1.05] text-balance mb-6">
                  The same strategy doesn't work in every country.
                </h2>
                <p className="text-zinc-500 leading-relaxed mb-6">
                  WhatsApp drives 40% of Brazilian sales. LINE is Japan's primary marketing channel. Germany's consumers trust organic search far more than ads. UAE audiences respond to Instagram influencer campaigns at 3× the rate of European markets.
                </p>
                <p className="text-zinc-500 leading-relaxed mb-8">
                  Generic "global" agencies run the same playbook in every market. We don't. Every country engagement starts with local channel mapping, local competitor analysis, and local keyword research in the native language.
                </p>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 bg-electric text-white px-6 py-3.5 font-semibold rounded-xl hover:bg-electric-500 transition-all shadow-lg shadow-electric/30"
                >
                  Talk to a local specialist <ArrowRight className="w-4 h-4" />
                </Link>
              </div>

              <div className="lg:col-span-6 grid grid-cols-2 gap-4">
                {[
                  { market: "🇧🇷 Brazil", channel: "WhatsApp Business", insight: "98% smartphone penetration — WhatsApp outperforms email 4× for lead conversion." },
                  { market: "🇯🇵 Japan", channel: "LINE Ads", insight: "92M LINE users — LINE is the #1 direct marketing channel for Japanese consumers." },
                  { market: "🇩🇪 Germany", channel: "Organic SEO", insight: "40% less paid ad competition — German buyers trust search rankings over ads." },
                  { market: "🇦🇪 UAE", channel: "Instagram + Influencer", insight: "4.7× higher Instagram CPM — offset by premium conversion and order values." },
                ].map((item) => (
                  <motion.div
                    key={item.market}
                    initial={{ opacity: 0, y: 12 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.35 }}
                    className="rounded-2xl border border-zinc-100 bg-white p-5 shadow-card"
                  >
                    <p className="font-display font-extrabold text-charcoal text-base mb-1">{item.market}</p>
                    <p className="text-xs text-electric font-semibold uppercase tracking-widest mb-3">{item.channel}</p>
                    <p className="text-xs text-zinc-500 leading-relaxed">{item.insight}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ══════════════════════════════════════ DON'T SEE YOUR COUNTRY */}
        <section className="py-16 md:py-20 bg-white border-b border-zinc-100">
          <div className="container">
            <div className="grid lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-8">
                <p className="text-overline text-electric mb-3">Don't see your market?</p>
                <h2 className="font-display text-2xl md:text-4xl font-extrabold text-charcoal leading-[1.1]">
                  We run campaigns in 20+ countries beyond the top 10.
                </h2>
                <p className="mt-4 text-zinc-500 leading-relaxed max-w-2xl">
                  France, Spain, Italy, Mexico, South Korea, Netherlands, South Africa,
                  Philippines, Turkey, Saudi Arabia — and more. Tell us your target market
                  and we'll build a localized plan within 24 hours.
                </p>
                <div className="mt-5 flex flex-wrap gap-2">
                  {["🇫🇷 France","🇪🇸 Spain","🇲🇽 Mexico","🇰🇷 South Korea","🇳🇱 Netherlands","🇸🇦 Saudi Arabia"].map(c => (
                    <span key={c} className="text-sm border border-zinc-100 bg-zinc-50 rounded-full px-3 py-1.5 text-zinc-600">
                      {c}
                    </span>
                  ))}
                  <span className="text-sm border border-zinc-100 bg-zinc-50 rounded-full px-3 py-1.5 text-zinc-400">
                    + 14 more
                  </span>
                </div>
              </div>
              <div className="lg:col-span-4 flex lg:justify-end">
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 bg-electric text-white px-6 py-3.5 font-semibold rounded-xl hover:bg-electric-500 hover:-translate-y-0.5 transition-all shadow-lg shadow-electric/30"
                >
                  Talk to the team <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </section>

        <CTABanner />
      </main>
      <Footer />
    </>
  );
}
